import React, { memo } from 'react';
import { Calendar, MapPin, Clock, Users, FileText, Video, Mail, Phone } from 'lucide-react';
import { convertYouTubeUrlToEmbed } from '../utils/videoUtils';
import PrivacyDisclaimerText from '../../../Common/EventRegistration/PrivacyDisclaimerText';

/**
 * Preview panel component showing live preview of landing page
 */
const PreviewPanel = memo(({ eventData = {}, colorScheme, previewDevice, registrationForm }) => {
  // Ensure eventData and colorScheme are always objects
  const safeEventData = eventData || {};
  const safeColorScheme = colorScheme || { colors: ['#f97316', '#fb923c', '#fdba74', '#fed7aa'] };
  const formFields = registrationForm || [];
  const titleColor =
    (safeEventData.eventTitleColor && safeEventData.eventTitleColor.trim()) ||
    safeColorScheme.colors?.[0] ||
    '#ffffff';
  
  // Debug: Log support data
  if (process.env.NODE_ENV === 'development') {
    console.log('[PreviewPanel] Support data:', {
      supportMail: safeEventData.supportMail,
      supportPhoneNumber: safeEventData.supportPhoneNumber,
      hasSupportMail: !!(safeEventData.supportMail && safeEventData.supportMail.trim()),
      hasSupportPhone: !!(safeEventData.supportPhoneNumber && safeEventData.supportPhoneNumber.trim()),
    });
  }

  return (
    <div className="landing-preview-panel">
      <div className={`landing-preview-frame ${previewDevice || 'desktop'}`}>
        <div className="landing-preview-content">
          <div
            className="landing-preview-hero"
            style={{
              background: safeEventData.bannerImage
                ? `url(${safeEventData.bannerImage}) center/cover no-repeat, linear-gradient(135deg, ${safeColorScheme.colors[0]}, ${safeColorScheme.colors[1]})`
                : `linear-gradient(135deg, ${safeColorScheme.colors[0]}, ${safeColorScheme.colors[1]})`,
              position: 'relative',
            }}
          >
            {safeEventData.bannerImage && (
              <div
                style={{
                  position: 'absolute',
                  top: 0,
                  left: 0,
                  right: 0,
                  bottom: 0,
                  backgroundColor: 'rgba(0, 0, 0, 0.4)',
                  zIndex: 1,
                }}
              />
            )}
            <div style={{ position: 'relative', zIndex: 2 }}>
              <h1 style={{ color: titleColor }}>
                {safeEventData.title || 'Event Title'}
              </h1>
              <p>{safeEventData.subtitle || 'Event Subtitle'}</p>
              <div className="landing-preview-details">
                {safeEventData.date && (
                  <span>
                    <Calendar size={14} />
                    {new Date(safeEventData.date).toLocaleDateString()}
                  </span>
                )}
                {safeEventData.time && (
                  <span>
                    <Clock size={14} /> {safeEventData.time}
                  </span>
                )}
                {safeEventData.location && (
                  <span>
                    <MapPin size={14} /> {safeEventData.location}
                  </span>
                )}
                {safeEventData.capacity && (
                  <span>
                    <Users size={14} /> {safeEventData.capacity} attendees
                  </span>
                )}
              </div>
              <button className="landing-preview-cta">
                {safeEventData.ctaText || 'Register Now'}
              </button>
            </div>
          </div>

          <div className="landing-preview-section">
            <h2>About This Event</h2>
            <p
              style={{
                textAlign: 'center',
                marginBottom: '32px',
                color: 'var(--text-light)',
              }}
            >
              {safeEventData.description || 'Event description will appear here.'}
            </p>

            {/* Video Section */}
            {safeEventData.videoLink && (
              <div
                style={{
                  marginBottom: '32px',
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                }}
              >
                <h3
                  style={{
                    fontSize: '1.5rem',
                    marginBottom: '1rem',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '0.5rem',
                  }}
                >
                  <Video size={20} />
                  Event Video
                </h3>
                <div
                  style={{
                    position: 'relative',
                    width: '100%',
                    maxWidth: '560px',
                    paddingBottom: '56.25%', // 16:9 aspect ratio
                    height: 0,
                    overflow: 'hidden',
                    borderRadius: '8px',
                    boxShadow: '0 4px 6px rgba(0, 0, 0, 0.1)',
                  }}
                >
                  <iframe
                    width="100%"
                    height="100%"
                    src={convertYouTubeUrlToEmbed(safeEventData.videoLink) || safeEventData.videoLink}
                    title="Event Video"
                    frameBorder="0"
                    allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
                    allowFullScreen
                    style={{
                      position: 'absolute',
                      top: 0,
                      left: 0,
                      width: '100%',
                      height: '100%',
                    }}
                  />
                </div>
              </div>
            )}

            {safeEventData.features && safeEventData.features.length > 0 && (
              <>
                <h3>What to Expect</h3>
                <div className="landing-preview-grid">
                  {safeEventData.features.map((feature, index) => (
                    <div key={index} className="landing-preview-feature">
                      <span style={{ color: safeColorScheme.colors[0] }}>✓</span>
                      {feature}
                    </div>
                  ))}
                </div>
              </>
            )}
          </div>

          {/* Agenda preview (time range per item) */}
          {safeEventData.agenda && safeEventData.agenda.length > 0 && (
            <div className="landing-preview-section">
              <h3>Event Agenda</h3>
              <div
                style={{
                  display: 'flex',
                  flexDirection: 'column',
                  gap: '8px',
                  maxWidth: '640px',
                  margin: '0 auto',
                }}
              >
                {safeEventData.agenda.map((item, index) => (
                  <div
                    key={index}
                    style={{
                      display: 'flex',
                      alignItems: 'flex-start',
                      gap: '12px',
                      padding: '10px 12px',
                      borderRadius: '8px',
                      backgroundColor: '#f9fafb',
                      border: '1px solid #e5e7eb',
                    }}
                  >
                    <div
                      style={{
                        minWidth: '100px',
                        fontWeight: 600,
                        fontSize: '0.85rem',
                        color: safeColorScheme.colors[0],
                      }}
                    >
                      {item.startTime && item.endTime
                        ? `${item.startTime} - ${item.endTime}`
                        : item.time || '--:--'}
                    </div>
                    <div style={{ flex: 1 }}>
                      <div
                        style={{
                          fontWeight: 600,
                          fontSize: '0.95rem',
                          marginBottom: item.description ? 4 : 0,
                          color: '#111827',
                        }}
                      >
                        {item.title || 'Agenda item'}
                      </div>
                      {item.description && (
                        <div
                          style={{
                            fontSize: '0.85rem',
                            color: '#4b5563',
                          }}
                        >
                          {item.description}
                        </div>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {safeEventData.speakers && safeEventData.speakers.length > 0 && (
            <div className="landing-preview-section">
              <h3>Featured Speakers</h3>
              <div className="landing-preview-speakers">
                {safeEventData.speakers.map((speaker, index) => (
                  <div key={index} className="landing-preview-speaker">
                    <div
                      className="landing-speaker-avatar"
                      style={{ backgroundColor: safeColorScheme.colors[0] }}
                    >
                      {speaker.avatar || (speaker.name ? speaker.name.charAt(0) : '?')}
                    </div>
                    <div>
                      <div className="landing-speaker-name">
                        {speaker.name || 'Speaker Name'}
                      </div>
                      <div className="landing-speaker-title">
                        {speaker.title || 'Speaker Title'}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Support Section */}
          {((safeEventData.supportMail && safeEventData.supportMail.trim()) || 
            (safeEventData.supportPhoneNumber && safeEventData.supportPhoneNumber.trim())) && (
            <div className="landing-preview-section">
              <h3>
                <Mail size={20} style={{ marginRight: '8px', verticalAlign: 'middle' }} />
                Event Support
              </h3>
              <div
                style={{
                  display: 'flex',
                  flexDirection: 'column',
                  gap: '12px',
                  maxWidth: '600px',
                  margin: '0 auto',
                }}
              >
                {safeEventData.supportMail && (
                  <div
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: '12px',
                      padding: '12px',
                      backgroundColor: '#f9fafb',
                      borderRadius: '8px',
                    }}
                  >
                    <Mail size={18} style={{ color: safeColorScheme.colors[0] }} />
                    <div>
                      <div style={{ fontSize: '0.875rem', color: '#6b7280', marginBottom: '2px' }}>
                        Email
                      </div>
                      <a
                        href={`mailto:${safeEventData.supportMail}`}
                        style={{
                          color: safeColorScheme.colors[0],
                          textDecoration: 'none',
                          fontWeight: '500',
                        }}
                      >
                        {safeEventData.supportMail}
                      </a>
                    </div>
                  </div>
                )}
                {safeEventData.supportPhoneNumber && (
                  <div
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: '12px',
                      padding: '12px',
                      backgroundColor: '#f9fafb',
                      borderRadius: '8px',
                    }}
                  >
                    <Phone size={18} style={{ color: safeColorScheme.colors[0] }} />
                    <div>
                      <div style={{ fontSize: '0.875rem', color: '#6b7280', marginBottom: '2px' }}>
                        Phone
                      </div>
                      <a
                        href={`tel:${safeEventData.supportPhoneNumber}`}
                        style={{
                          color: safeColorScheme.colors[0],
                          textDecoration: 'none',
                          fontWeight: '500',
                        }}
                      >
                        {safeEventData.supportPhoneNumber}
                      </a>
                    </div>
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Registration Form Section */}
          {formFields && formFields.length > 0 && (
            <div className="landing-preview-section">
              <h3>
                <FileText size={20} style={{ marginRight: '8px', verticalAlign: 'middle' }} />
                Registration Form
              </h3>
              <div className="landing-preview-form">
                <form style={{ maxWidth: '600px', margin: '0 auto' }}>
                  {/* Standard fields (first 4) */}
                  <div className="landing-preview-form-row">
                    <div className="landing-preview-form-field">
                      <label>First name *</label>
                      <input type="text" placeholder="First name" disabled />
                    </div>
                    <div className="landing-preview-form-field">
                      <label>Last name *</label>
                      <input type="text" placeholder="Last name" disabled />
                    </div>
                  </div>
                  <div className="landing-preview-form-row">
                    <div className="landing-preview-form-field">
                      <label>Email *</label>
                      <input type="email" placeholder="Email" disabled />
                    </div>
                    <div className="landing-preview-form-field">
                      <label>Phone number *</label>
                      <input type="tel" placeholder="Phone number" disabled />
                    </div>
                  </div>

                  {/* Custom fields (after first 4) */}
                  {formFields.slice(4).map((field, index) => (
                    <div key={field._id || index} className="landing-preview-form-field-full">
                      {field.question_type !== 'checkbox' &&
                        field.question_type !== 'privacy_disclaimer' && (
                        <label>{field.label || 'Field'}</label>
                      )}
                      {field.question_type === 'text' || field.question_type === 'short_answer' ? (
                        <input
                          type="text"
                          placeholder={field.label}
                          disabled
                        />
                      ) : field.question_type === 'paragraph' ? (
                        <textarea
                          rows={3}
                          placeholder={field.label}
                          disabled
                        />
                      ) : field.question_type === 'options' ? (
                        <select disabled>
                          <option>Select...</option>
                          {field.list && field.list.map((option, optIndex) => (
                            <option key={optIndex} value={option}>
                              {option}
                            </option>
                          ))}
                        </select>
                      ) : field.question_type === 'checkbox' ? (
                        <label style={{ display: "flex", alignItems: "center", gap: "8px", margin: 0 }}>
                          <input type="checkbox" disabled />
                          {field.label || 'Checkbox'}
                        </label>
                      ) : field.question_type === 'privacy_disclaimer' ? (
                        <PrivacyDisclaimerText linkUrl={field.linkUrl} />
                      ) : (
                        <input type="text" placeholder={field.label} disabled />
                      )}
                    </div>
                  ))}

                  <button
                    type="button"
                    className="landing-preview-form-submit"
                    style={{
                      backgroundColor: safeColorScheme.colors[0],
                      color: 'white',
                      border: 'none',
                      padding: '12px 32px',
                      borderRadius: '8px',
                      fontSize: '16px',
                      fontWeight: '600',
                      cursor: 'not-allowed',
                      marginTop: '20px',
                      width: '100%',
                      opacity: 0.7,
                    }}
                    disabled
                  >
                    Submit Registration
                  </button>
                </form>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
});

PreviewPanel.displayName = 'PreviewPanel';

export default PreviewPanel;

